import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { TranslocoService } from "@ngneat/transloco";
import { AuthService } from "src/app/service/auth.service";
import { SocialService } from "src/app/service/social.service";
import { checkUpload, UPLOAD_RULES } from "src/app/shared/upload/upload-policy";
import { isHdrVideo } from "src/app/shared/upload/video-hdr";
import { AccountService } from "src/app/service/account.service";
import {
  ProjectMiniResponse,
  ProjectSearchHitResponse,
  SocialCreatePostRequest,
  SocialPostResponse,
  SocialSearchUserHit,
} from "src/app/model/social/social.types";
import { MessageService } from "primeng/api";
import { CropTransform, cropImageToFile, defaultCropTransform } from "../post-image-cropper/image-crop.util";
import {
  canRefineLocally,
  filterUserHitsByPrefix,
  SOCIAL_SEARCH_MIN_CHARS,
  SOCIAL_SEARCH_TAKE,
  UserSearchSnapshot,
} from "./user-search-refine.util";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { forkJoin, lastValueFrom, of, Subject } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  tap,
} from "rxjs/operators";
import { GroupedNotification, NotificationSseEvent } from "src/app/model/notification.model";
import { NotificationService } from "src/app/service/notification.service";
import { ImageUrlService } from "src/app/service/image-url.service";

type FeedTab = "feed" | "explore";

const GUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Marca `viewerFollowsProject` em todos os posts do projeto (sem trocar as referências dos posts). */
export function markProjectFollowed(posts: SocialPostResponse[], projectId: string): void {
  for (const p of posts) {
    if (p.projectId === projectId) p.viewerFollowsProject = true;
  }
}

@Component({
  selector: "app-social-feed",
  templateUrl: "./social-feed.component.html",
  styleUrls: ["./social-feed.component.css"],
})
export class SocialFeedComponent implements OnInit, OnDestroy {
  activeTab: FeedTab = "feed";
  /** 403 SOCIAL_SUSPENDED / SOCIAL_BANNED: o viewer foi sancionado no feed pela moderação (só o feed; o resto do app segue). */
  socialAccessBlocked: "suspended" | "banned" | null = null;

  posts: SocialPostResponse[] = [];
  nextCursor: string | null = null;
  loading = false;
  loadingMore = false;
  /** Blob URLs temporárias do post recém-criado — revogadas no próximo loadFeed ou ngOnDestroy. */
  private _pendingBlobUrls: string[] = [];

  explorePosts: SocialPostResponse[] = [];
  exploreCursor: string | null = null;
  exploreLoading = false;
  exploreLoadingMore = false;
  private exploreUsingRecentSort = false;

  currentUserId?: string;
  /** URL do avatar do usuário autenticado (presigned S3 ou blob local). */
  currentUserAvatarUrl: string | null = null;
  /** Nome/nickname para UI (modal criar post); evita mostrar apenas o GUID. */
  currentUserDisplayName = "";

  searchQuery = "";
  searchLoading = false;
  searchSuggestions: SocialSearchUserHit[] = [];
  searchSuggestionsProjects: ProjectSearchHitResponse[] = [];
  searchSuggestionsLoading = false;
  searchDropdownOpen = false;
  searchShowEmptyHint = false;
  /** Último resultado de usuários do servidor; permite refinar no cliente quando só se acrescenta letras. */
  private lastUserSearch: UserSearchSnapshot | null = null;
  private readonly searchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private searchBlurTimer: ReturnType<typeof setTimeout> | null = null;

  /** Dicas na barra lateral (desktop): índice 0..3, alterna a cada 9s */
  sidebarTipIndex = 0;
  readonly sidebarTipIndices = [0, 1, 2, 3];
  private tipRotateTimer: ReturnType<typeof setInterval> | null = null;

  private feedObserver: IntersectionObserver | undefined;
  private exploreObserver: IntersectionObserver | undefined;

  @ViewChild('feedSentinel') set feedSentinelRef(el: ElementRef | undefined) {
    this.feedObserver?.disconnect();
    this.feedObserver = undefined;
    if (el?.nativeElement) {
      this.feedObserver = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) this.loadMore(); },
        { rootMargin: '200px' }
      );
      this.feedObserver.observe(el.nativeElement);
    }
  }

  @ViewChild('exploreSentinel') set exploreSentinelRef(el: ElementRef | undefined) {
    this.exploreObserver?.disconnect();
    this.exploreObserver = undefined;
    if (el?.nativeElement) {
      this.exploreObserver = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) this.loadMoreExplore(); },
        { rootMargin: '200px' }
      );
      this.exploreObserver.observe(el.nativeElement);
    }
  }

  /** Notificações só de tipos sociais (curtida, comentário, follow, marcação) na lateral desktop */
  socialFeedNotifications: GroupedNotification[] = [];
  loadingSocialNotifications = false;
  private readonly socialNotifSidebarLimit = 10;
  private readonly socialNotifFetchSize = 30;

  createVisible = false;
  createStep: "noProject" | "pickProject" | "select" | "details" = "select";
  createCaption = "";
  createCaptionLength = 0;
  readonly captionMaxLength = 300;
  /** Limite de mídias (fotos/vídeos) por publicação. */
  readonly maxMediaCount = 10;

  /** Projetos públicos do viewer disponíveis para postar (popula seletor). */
  myPublicProjects: ProjectMiniResponse[] = [];
  /** Projeto atualmente selecionado para a publicação. */
  selectedProjectId: string | null = null;
  /** Carregando lista de projetos públicos do viewer. */
  loadingMyProjects = false;
  pendingFiles: File[] = [];
  previewUrls: string[] = [];
  currentPreview = 0;

  // ----- Recorte estilo Instagram (SÓ imagem; vídeo não é redimensionado) -----
  /** Proporção (largura/altura) única do post; aplicada a todas as imagens. */
  postAspect = 1;
  readonly aspectOptions: { label: string; nameKey: string; value: number }[] = [
    { label: "1:1", nameKey: "socialFeed.ratioSquare", value: 1 },
    { label: "4:5", nameKey: "socialFeed.ratioPortrait", value: 0.8 },
    { label: "1.91", nameKey: "socialFeed.ratioLandscape", value: 1.91 },
  ];
  /** Lado maior do desenho da proporcao, em px. */
  private readonly aspectShapeBox = 18;

  /* O desenho sai do proprio numero da proporcao, entao qualquer opcao nova
     ganha a forma certa sem tabela paralela para manter em dia. */
  aspectShapeW(value: number): number {
    return value >= 1 ? this.aspectShapeBox : Math.round(this.aspectShapeBox * value);
  }

  aspectShapeH(value: number): number {
    return value >= 1 ? Math.round(this.aspectShapeBox / value) : this.aspectShapeBox;
  }
  /** Transformação (scale, cx, cy) por mídia — paralelo a pendingFiles/previewUrls. */
  cropTransforms: CropTransform[] = [];
  private readonly imageFrameMax = 470;
  /** Dimensões do quadro de imagem no compositor, derivadas da proporção escolhida. */
  get imageFrameW(): number {
    return this.postAspect >= 1 ? this.imageFrameMax : Math.round(this.imageFrameMax * this.postAspect);
  }
  get imageFrameH(): number {
    return this.postAspect >= 1 ? Math.round(this.imageFrameMax / this.postAspect) : this.imageFrameMax;
  }
  setAspect(v: number): void { this.postAspect = v; }
  isFileImage(index: number): boolean {
    return this.pendingFiles[index]?.type.startsWith("image/") ?? false;
  }

  /** Reordena as mídias arrastando as miniaturas (mantém arquivos, previews e recortes em sincronia). */
  onThumbDrop(event: CdkDragDrop<unknown>): void {
    const from = event.previousIndex;
    const to = event.currentIndex;
    if (from === to) return;
    moveItemInArray(this.pendingFiles, from, to);
    moveItemInArray(this.previewUrls, from, to);
    moveItemInArray(this.cropTransforms, from, to);
    // Mantém a mídia atualmente exibida apontando para o mesmo arquivo após a reordenação.
    if (this.currentPreview === from) {
      this.currentPreview = to;
    } else if (from < this.currentPreview && to >= this.currentPreview) {
      this.currentPreview--;
    } else if (from > this.currentPreview && to <= this.currentPreview) {
      this.currentPreview++;
    }
  }
  /** Dimensões naturais da primeira mídia (definem o quadro do carrossel). */
  private firstMediaNaturalW = 0;
  private firstMediaNaturalH = 0;
  /** Tamanho exibido do quadro (escala da primeira foto para caber no modal). */
  previewFrameW = 0;
  previewFrameH = 0;
  creating = false;
  /** Etapa atual da publicação para feedback visual no modal. */
  creatingStep: 'uploading' | 'publishing' | null = null;
  /** Índice (1-based) do arquivo atualmente em upload. */
  creatingFileIndex = 0;
  /** Total de arquivos a enviar. */
  creatingFileTotal = 0;
  isDragging = false;



  constructor(
    private social: SocialService,
    private auth: AuthService,
    private accountService: AccountService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: NotificationService,
    private translocoService: TranslocoService,
    private imageUrlService: ImageUrlService,
    private titleService: Title
  ) {}

  ngOnInit(): void {
    this.setTitle();
    this.currentUserId = this.auth.getUserInformations();
    this.loadCurrentUserDisplay();
    this.loadCurrentUserAvatar();
    this.route.queryParamMap.subscribe((qp) => {
      if (qp.get("tab") === "explore") {
        this.activeTab = "explore";
      }
      // Deep link: /feed?compose=<projectId> abre o modal já com o projeto pré-selecionado
      const compose = qp.get("compose");
      if (compose) {
        this.openCreate(compose);
      }
      // Deep link legado de notificação: /feed?post=<id> redireciona para o detalhe do post
      const post = qp.get("post");
      if (post) {
        this.router.navigate(["/feed/explore", post]);
      }
    });
    this.loadFeed(true);
    this.loadExplore(true);
    this.loadSocialFeedNotifications();

    this.notificationService.notifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (!event.type?.startsWith("Social")) {
          return;
        }
        const g = this.sseEventToGrouped(event);
        const id0 = g.notificationIds[0];
        const rest = this.socialFeedNotifications.filter((x) => x.notificationIds[0] !== id0);
        this.socialFeedNotifications = [g, ...rest].slice(0, this.socialNotifSidebarLimit);
      });

    this.searchInput$
      .pipe(
        debounceTime(280),
        distinctUntilChanged(),
        switchMap((raw) => {
          const q = raw.trim();
          // Abaixo do mínimo o backend de usuários já responde vazio; antes o front disparava as
          // duas consultas mesmo assim e a de projetos rodava no banco sem chance de resultado útil.
          if (q.length < SOCIAL_SEARCH_MIN_CHARS) {
            this.lastUserSearch = null;
            this.searchSuggestions = [];
            this.searchSuggestionsProjects = [];
            this.searchShowEmptyHint = false;
            this.searchDropdownOpen = false;
            return of<{ users: SocialSearchUserHit[]; projects: ProjectSearchHitResponse[]; q: string }>(
              { users: [], projects: [], q }
            );
          }
          this.searchSuggestionsLoading = true;
          this.searchShowEmptyHint = false;
          // Usuários: se o resultado anterior já é completo e a query só cresceu, filtra localmente.
          // Projetos sempre vão ao servidor: a busca por nome é FULLTEXT por palavra, não é monótona.
          const users$ = canRefineLocally(this.lastUserSearch, q)
            ? of(filterUserHitsByPrefix(this.lastUserSearch!.hits, q))
            : this.social.searchUsers(q, SOCIAL_SEARCH_TAKE);
          return forkJoin({
            users: users$,
            projects: this.social.searchProjects(q, SOCIAL_SEARCH_TAKE),
          }).pipe(
            map(({ users, projects }) => {
              this.lastUserSearch = { query: q, hits: users, take: SOCIAL_SEARCH_TAKE };
              return { users, projects, q };
            }),
            tap(() => {
              this.searchSuggestionsLoading = false;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(({ users, projects, q }) => {
        const trimmed = q.trim();
        this.searchSuggestions = users;
        this.searchSuggestionsProjects = projects;
        this.searchShowEmptyHint =
          trimmed.length >= SOCIAL_SEARCH_MIN_CHARS && users.length === 0 && projects.length === 0;
        this.searchDropdownOpen =
          trimmed.length >= SOCIAL_SEARCH_MIN_CHARS &&
          (users.length > 0 || projects.length > 0 || this.searchShowEmptyHint);
      });

    this.tipRotateTimer = setInterval(() => {
      this.sidebarTipIndex = (this.sidebarTipIndex + 1) % this.sidebarTipIndices.length;
    }, 9000);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.searchBlurTimer) {
      clearTimeout(this.searchBlurTimer);
    }
    if (this.tipRotateTimer) {
      clearInterval(this.tipRotateTimer);
      this.tipRotateTimer = null;
    }
    this.feedObserver?.disconnect();
    this.exploreObserver?.disconnect();
    this._pendingBlobUrls.forEach(u => URL.revokeObjectURL(u));
    this._pendingBlobUrls = [];
  }

  setTitle() {
    this.translocoService
      .selectTranslate("title.feed")
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.titleService.setTitle(value));
  }

  /** Chave Transloco para a dica atual (`socialFeed.sidebarTip1` … `sidebarTip4`). */
  get tipTranslationKey(): string {
    return `socialFeed.sidebarTip${this.sidebarTipIndex + 1}`;
  }

  /** Até 3 posts do Explorar com mais curtidas (MVP “em alta”). */
  get trendingRailPosts(): SocialPostResponse[] {
    if (!this.explorePosts?.length) {
      return [];
    }
    return [...this.explorePosts]
      .sort((a, b) => (b.likesCount ?? 0) - (a.likesCount ?? 0))
      .slice(0, 3);
  }

  displayAuthor(post: SocialPostResponse): string {
    const nick = post.authorNickname?.trim();
    const name = post.authorName?.trim();
    if (nick) {
      return nick;
    }
    if (name) {
      return name;
    }
    return post.authorUserId;
  }

  /** Carrega o avatarUrl do usuário autenticado para exibição nos post cards. */
  private loadCurrentUserAvatar(): void {
    const uid = this.currentUserId;
    if (!uid) return;
    this.social.getProfile(uid).pipe(takeUntil(this.destroy$)).subscribe({
      next: (p) => {
        this.currentUserAvatarUrl = p.avatarUrl ?? null;
      },
      error: () => {},
    });
  }

  /** Preenche `currentUserDisplayName` a partir de `/user/active` ou do perfil social. */
  private loadCurrentUserDisplay(): void {
    const uid = this.currentUserId;
    if (!uid) {
      return;
    }
    this.auth
      .getUserData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (u) => {
          const nick = u.nickname?.trim();
          const name = u.name?.trim();
          this.currentUserDisplayName = nick || name || uid;
        },
        error: () => {
          this.social
            .getProfile(uid)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (p) => {
                const nick = p.nickname?.trim();
                const name = p.name?.trim();
                this.currentUserDisplayName = nick || name || uid;
              },
              error: () => {
                this.currentUserDisplayName = uid;
              },
            });
        },
      });
  }

  onSearchQueryChange(value: string): void {
    this.searchInput$.next(value ?? "");
  }

  onSearchFocus(): void {
    if (this.searchBlurTimer) {
      clearTimeout(this.searchBlurTimer);
      this.searchBlurTimer = null;
    }
    const q = this.searchQuery.trim();
    if (q.length >= 1) {
      this.searchDropdownOpen =
        this.searchSuggestionsLoading ||
        this.searchSuggestions.length > 0 ||
        this.searchShowEmptyHint;
    }
  }

  onSearchBlur(): void {
    this.searchBlurTimer = setTimeout(() => {
      this.searchDropdownOpen = false;
    }, 200);
  }

  goToProfileFromSearch(user: SocialSearchUserHit, event?: Event): void {
    event?.preventDefault();
    if (this.searchBlurTimer) {
      clearTimeout(this.searchBlurTimer);
      this.searchBlurTimer = null;
    }
    this.searchDropdownOpen = false;
    this.searchQuery = "";
    this.searchInput$.next("");
    this.searchSuggestions = [];
    this.searchSuggestionsProjects = [];
    this.router.navigate(["/feed/profile", user.userId]);
  }

  /** Navegar até o perfil do projeto selecionado no autocomplete. */
  goToProjectFromSearch(project: ProjectSearchHitResponse, event?: Event): void {
    event?.preventDefault();
    if (this.searchBlurTimer) {
      clearTimeout(this.searchBlurTimer);
      this.searchBlurTimer = null;
    }
    this.searchDropdownOpen = false;
    this.searchQuery = "";
    this.searchInput$.next("");
    this.searchSuggestions = [];
    this.searchSuggestionsProjects = [];
    this.router.navigate(["/feed/project", project.projectId]);
  }

  displayName(u: SocialSearchUserHit): string {
    const nick = u.nickname?.trim();
    const name = u.name?.trim();
    if (nick && name) {
      return `${nick} · ${name}`;
    }
    return nick || name || u.userId;
  }

  switchTab(tab: FeedTab): void {
    this.activeTab = tab;
  }

  goToMyProfile(): void {
    if (this.currentUserId) {
      this.router.navigate(["/feed/profile", this.currentUserId]);
    }
  }

  searchUser(): void {
    const query = this.searchQuery.trim();
    if (!query) return;

    if (query.includes("@")) {
      this.searchLoading = true;
      this.accountService.FindUserEmail(query).subscribe({
        next: (res) => {
          this.searchLoading = false;
          if (res?.id) {
            this.router.navigate(["/feed/profile", res.id]);
            this.searchQuery = "";
          } else {
            this.messageService.add({
              severity: "warn",
              summary: this.translocoService.translate('socialFeed.searchLabel'),
              detail: this.translocoService.translate('socialFeed.searchNotFound'),
            });
          }
        },
        error: () => {
          this.searchLoading = false;
          this.messageService.add({
            severity: "warn",
            summary: this.translocoService.translate('socialFeed.searchLabel'),
            detail: this.translocoService.translate('socialFeed.searchNotFound'),
          });
        },
      });
      return;
    }

    if (GUID_LIKE.test(query)) {
      this.searchLoading = true;
      this.social.getProfile(query).subscribe({
        next: (profile) => {
          this.searchLoading = false;
          if (profile?.userId) {
            this.router.navigate(["/feed/profile", profile.userId]);
            this.searchQuery = "";
          } else {
            this.messageService.add({
              severity: "warn",
              summary: this.translocoService.translate('socialFeed.searchLabel'),
              detail: this.translocoService.translate('socialFeed.searchNotFound'),
            });
          }
        },
        error: () => {
          this.searchLoading = false;
          this.messageService.add({
            severity: "warn",
            summary: this.translocoService.translate('socialFeed.searchLabel'),
            detail: this.translocoService.translate('socialFeed.searchNotFound'),
          });
        },
      });
      return;
    }

    this.searchLoading = true;
    this.social.searchUsers(query, 15).subscribe({
      next: (users) => {
        this.searchLoading = false;
        if (users.length === 1) {
          this.goToProfileFromSearch(users[0]);
          return;
        }
        if (users.length > 1) {
          this.searchSuggestions = users;
          this.searchDropdownOpen = true;
          this.searchShowEmptyHint = false;
          return;
        }
        this.messageService.add({
          severity: "warn",
          summary: this.translocoService.translate('socialFeed.searchLabel'),
          detail: this.translocoService.translate('socialFeed.searchNotFoundHint'),
        });
      },
      error: () => {
        this.searchLoading = false;
        this.messageService.add({
          severity: "warn",
          summary: this.translocoService.translate('socialFeed.searchLabel'),
          detail: this.translocoService.translate('socialFeed.searchError'),
        });
      },
    });
  }

  /* ---- FEED (backend already mixes: following + sponsored + trending) ---- */

  /** Reconhece o 403 do SocialSanctionFilter e troca o toast de erro pelo aviso fixo no topo do feed. */
  private markSocialAccessBlocked(err: unknown): boolean {
    const code = (err as { error?: { error?: unknown } } | null)?.error?.error;
    if (code === "SOCIAL_SUSPENDED") this.socialAccessBlocked = "suspended";
    else if (code === "SOCIAL_BANNED") this.socialAccessBlocked = "banned";
    else return false;
    return true;
  }

  loadFeed(reset: boolean): void {
    if (reset) {
      // Revoga blob URLs do post temporário antes de substituir a lista
      this._pendingBlobUrls.forEach(u => URL.revokeObjectURL(u));
      this._pendingBlobUrls = [];
      this.posts = [];
      this.nextCursor = null;
    }
    this.loading = true;
    this.social.getFeed(20, reset ? undefined : this.nextCursor).subscribe({
      next: (res) => {
        this.posts = reset ? res.items : [...this.posts, ...res.items];
        this.nextCursor = res.nextCursor ?? null;
        this.loading = false;
        this.loadingMore = false;
      },
      error: (err) => {
        this.loading = false;
        this.loadingMore = false;
        if (this.markSocialAccessBlocked(err)) return;
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate('socialFeed.feedLabel'),
          detail: SocialService.parseHttpErrorMessage(err),
        });
      },
    });
  }

  private loadMore(): void {
    if (!this.nextCursor || this.loadingMore || this.loading) return;
    this.loadingMore = true;
    this.social.getFeed(20, this.nextCursor).subscribe({
      next: (res) => {
        this.posts = [...this.posts, ...res.items];
        this.nextCursor = res.nextCursor ?? null;
        this.loadingMore = false;
      },
      error: (err) => {
        this.loadingMore = false;
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate('socialFeed.feedLabel'),
          detail: SocialService.parseHttpErrorMessage(err),
        });
      },
    });
  }

  /* ---- EXPLORE ---- */

  loadExplore(reset: boolean): void {
    if (reset) {
      this.explorePosts = [];
      this.exploreCursor = null;
      this.exploreUsingRecentSort = false;
    }
    this.exploreLoading = true;
    this.social.getExplore(20, reset ? undefined : this.exploreCursor).subscribe({
      next: (res) => {
        this.explorePosts = reset ? res.items : [...this.explorePosts, ...res.items];
        this.exploreCursor = res.nextCursor ?? null;
        if (res.items.length < 5) {
          this.fetchRecentExploreFallback();
        } else {
          this.exploreLoading = false;
          this.exploreLoadingMore = false;
        }
      },
      error: (err) => {
        this.exploreLoading = false;
        this.exploreLoadingMore = false;
        if (this.markSocialAccessBlocked(err)) return;
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate('socialFeed.exploreLabel'),
          detail: SocialService.parseHttpErrorMessage(err),
        });
      },
    });
  }

  private loadMoreExplore(): void {
    if (!this.exploreCursor || this.exploreLoadingMore || this.exploreLoading) return;
    this.exploreLoadingMore = true;

    if (this.exploreUsingRecentSort) {
      this.social.getExplore(20, this.exploreCursor, 'recent').subscribe({
        next: (res) => {
          this.explorePosts = [...this.explorePosts, ...res.items];
          this.exploreCursor = res.nextCursor ?? null;
          this.exploreLoadingMore = false;
        },
        error: (err) => {
          this.exploreLoadingMore = false;
          this.messageService.add({
            severity: "error",
            summary: this.translocoService.translate('socialFeed.exploreLabel'),
            detail: SocialService.parseHttpErrorMessage(err),
          });
        },
      });
      return;
    }

    this.social.getExplore(20, this.exploreCursor).subscribe({
      next: (res) => {
        this.explorePosts = [...this.explorePosts, ...res.items];
        this.exploreCursor = res.nextCursor ?? null;
        if (res.items.length < 5) {
          this.fetchRecentExploreFallback();
        } else {
          this.exploreLoadingMore = false;
        }
      },
      error: (err) => {
        this.exploreLoadingMore = false;
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate('socialFeed.exploreLabel'),
          detail: SocialService.parseHttpErrorMessage(err),
        });
      },
    });
  }

  /** Explore por engajamento (últimos 30) rendeu pouco — completa com os posts mais recentes. */
  private fetchRecentExploreFallback(): void {
    this.social.getExplore(20, undefined, 'recent').subscribe({
      next: (res) => {
        const existingIds = new Set(this.explorePosts.map(p => p.id));
        const fresh = res.items.filter(p => !existingIds.has(p.id));
        this.explorePosts = [...this.explorePosts, ...fresh];
        this.exploreCursor = res.nextCursor ?? null;
        this.exploreUsingRecentSort = true;
        this.exploreLoading = false;
        this.exploreLoadingMore = false;
      },
      error: (err) => {
        this.exploreLoading = false;
        this.exploreLoadingMore = false;
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate('socialFeed.exploreLabel'),
          detail: SocialService.parseHttpErrorMessage(err),
        });
      },
    });
  }

  onPostChanged(): void {
    this.loadFeed(true);
    this.loadExplore(true);
    this.loadSocialFeedNotifications();
  }

  /** Seguiu por um card: os outros posts do mesmo projeto já na tela escondem o botão também. */
  onProjectFollowedFromCard(projectId: string): void {
    markProjectFollowed(this.posts, projectId);
    markProjectFollowed(this.explorePosts, projectId);
  }

  loadSocialFeedNotifications(): void {
    const uid = this.currentUserId;
    if (!uid) {
      return;
    }
    this.loadingSocialNotifications = true;
    this.notificationService.getGroupedNotifications(uid, 1, this.socialNotifFetchSize).subscribe({
      next: (page) => {
        const social = page.listItens.filter((g) => this.isSocialNotificationType(g.type));
        this.socialFeedNotifications = social.slice(0, this.socialNotifSidebarLimit);
        this.loadingSocialNotifications = false;
      },
      error: () => {
        this.loadingSocialNotifications = false;
      },
    });
  }

  /** Tipos `Social*` (exclui upload, system, etc. misturados no aggregated). */
  isSocialNotificationType(type: string): boolean {
    return typeof type === "string" && type.startsWith("Social");
  }

  onSidebarSocialNotificationClick(g: GroupedNotification): void {
    const uid = this.currentUserId;
    if (!uid) {
      return;
    }
    if (!g.isRead && g.notificationIds?.length) {
      this.notificationService.markBatchAsRead(uid, g.notificationIds).subscribe({
        next: () => {
          g.isRead = true;
          this.notificationService.refreshUnreadCount(uid);
        },
        error: () => {
          forkJoin(
            g.notificationIds.map((id) =>
              this.notificationService.markAsRead(uid, id).pipe(catchError(() => of(null)))
            )
          ).subscribe(() => {
            g.isRead = true;
            this.notificationService.refreshUnreadCount(uid);
          });
        },
      });
    }
    this.navigateFromGroupedNotification(g);
  }

  getSocialNotificationIcon(type: string): string {
    const map: Record<string, string> = {
      SocialPostLiked: "pi pi-heart",
      SocialPostCommented: "pi pi-comment",
      SocialUserFollowed: "pi pi-user-plus",
      SocialPostTagged: "pi pi-at",
    };
    return map[type] || "pi pi-bell";
  }

  private sseEventToGrouped(event: NotificationSseEvent): GroupedNotification {
    return {
      groupKey: null,
      notificationIds: [event.id],
      type: event.type,
      title: event.title,
      message: event.message,
      body: null,
      actors: [],
      totalActors: 0,
      isRead: event.isRead,
      createdAt: event.createdAt,
      metadata: event.metadata ? (event.metadata as Record<string, unknown>) : null,
    };
  }

  private navigateFromGroupedNotification(g: GroupedNotification): void {
    const meta = g.metadata ?? undefined;
    if (meta && typeof meta === "object") {
      const actionUrl = meta["actionUrl"];
      if (typeof actionUrl === "string" && actionUrl) {
        this.router.navigateByUrl(actionUrl);
        return;
      }
      const projectId = meta["projectId"];
      if (typeof projectId === "string" && projectId) {
        this.router.navigate(["/project", projectId]);
        return;
      }
      const postId = meta["postId"];
      if (typeof postId === "string" && postId) {
        this.router.navigate(["/feed/explore", postId]);
        return;
      }
      const profileId = meta["userId"] ?? meta["actorUserId"] ?? meta["followerUserId"];
      if (typeof profileId === "string" && profileId && g.type === "SocialUserFollowed") {
        this.router.navigate(["/feed/profile", profileId]);
        return;
      }
    }
    const gk = g.groupKey;
    if (gk && gk.includes(":")) {
      const idx = gk.indexOf(":");
      const kind = gk.slice(0, idx);
      const id = gk.slice(idx + 1);
      if (!id) {
        return;
      }
      if (kind === "SocialPostLiked" || kind === "SocialPostCommented" || kind === "SocialPostTagged") {
        this.router.navigate(["/feed/explore", id]);
        return;
      }
      if (kind === "SocialUserFollowed") {
        const actor = g.actors?.[0]?.userId;
        if (actor) {
          this.router.navigate(["/feed/profile", actor]);
        }
        return;
      }
    }
  }

  private topMedia(post: SocialPostResponse) {
    return [...(post.media || [])].sort((a, b) => a.sortOrder - b.sortOrder)[0] ?? null;
  }

  isVideoPost(post: SocialPostResponse): boolean {
    return this.topMedia(post)?.mediaType === 'Video';
  }

  /** Miniaturas que falharam ao carregar (poster ainda não gerado, URL assinada expirada) — caem no placeholder. */
  private readonly brokenThumbs = new Set<string>();

  markThumbBroken(postId: string): void {
    this.brokenThumbs.add(postId);
  }

  /** Miniatura pra grid/rail: imagem real ou poster do vídeo (URL assinada que a API já devolve em
   *  `thumbnailUrl`). Sem poster, placeholder — nunca o `.mp4`: usar o vídeo como capa baixava o
   *  original direto do S3, fora do CDN, só para desenhar um quadrado. */
  exploreThumb(post: SocialPostResponse): { url: string } | null {
    if (this.brokenThumbs.has(post.id)) return null;
    const m = this.topMedia(post);
    if (!m) return null;

    if (m.mediaType === 'Video') {
      const poster = this.imageUrlService.transformSocialUrl(m.thumbnailUrl, 'social');
      return poster ? { url: poster } : null;
    }

    const img = this.imageUrlService.transformSocialUrl(m.url, 'social');
    return img ? { url: img } : null;
  }

  openExplorePost(postId: string): void {
    this.router.navigate(["/feed/explore", postId]);
  }

  /* ---- CREATE POST (two-step flow) ---- */

  openCreate(preselectProjectId?: string | null): void {
    this.createCaption = "";
    this.createCaptionLength = 0;
    this.pendingFiles = [];
    this.previewUrls = [];
    this.currentPreview = 0;
    this.cropTransforms = [];
    this.postAspect = 1;
    this.resetPreviewFrame();
    this.selectedProjectId = preselectProjectId ?? null;
    this.createVisible = true;

    // Carregar projetos públicos do viewer e decidir o passo inicial
    this.loadingMyProjects = true;
    this.social.getMyPublicProjects(50).subscribe({
      next: (projects) => {
        this.myPublicProjects = projects ?? [];
        this.loadingMyProjects = false;

        // Deep link: projeto pré-selecionado vindo do botão "Publicar" do perfil de projeto
        if (preselectProjectId && this.myPublicProjects.some(p => p.projectId === preselectProjectId)) {
          this.selectedProjectId = preselectProjectId;
          this.createStep = "select";
          return;
        }

        if (this.myPublicProjects.length === 0) {
          this.createStep = "noProject";
        } else if (this.myPublicProjects.length === 1) {
          this.selectedProjectId = this.myPublicProjects[0].projectId;
          this.createStep = "select";
        } else {
          this.createStep = "pickProject";
        }
      },
      error: () => {
        this.loadingMyProjects = false;
        this.myPublicProjects = [];
        this.createStep = "noProject";
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate("socialFeed.error"),
          detail: this.translocoService.translate("messages.error.fetchAllProjectsError"),
        });
      },
    });
  }

  /** Confirma o projeto escolhido no passo "pickProject" e avança para a seleção de mídia. */
  confirmProjectSelection(projectId: string): void {
    this.selectedProjectId = projectId;
    this.createStep = "select";
  }

  /** Volta ao passo de troca de projeto (apenas se o viewer tem mais de 1). */
  changeProject(): void {
    if (this.myPublicProjects.length > 1) {
      this.createStep = "pickProject";
    }
  }

  /** Nome do projeto selecionado para exibição no badge. */
  getSelectedProjectName(): string {
    return (
      this.myPublicProjects.find((p) => p.projectId === this.selectedProjectId)?.projectName || ""
    );
  }

  /** Avatar do projeto selecionado para o badge. */
  getSelectedProjectImage(): string | null {
    return (
      this.myPublicProjects.find((p) => p.projectId === this.selectedProjectId)?.primaryImageUrl ||
      null
    );
  }

  /** Levar o usuário para o painel de criação de projeto. */
  goCreateProjectFromModal(): void {
    this.createVisible = false;
    this.router.navigate(["/my-garage"]);
  }

  @HostListener("window:resize")
  onCreateDialogResize(): void {
    if (this.createStep === "details" && this.firstMediaNaturalW > 0) {
      this.updatePreviewFrameSize();
    }
  }

  onCaptionInput(): void {
    this.createCaptionLength = this.createCaption.length;
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.addFiles(Array.from(input.files));
    }
    input.value = "";
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(): void {
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.addFiles(Array.from(files));
    }
  }

  private static readonly MAX_VIDEO_SECONDS = 60; // 1 minuto (duração; tipo/tamanho via política central)

  private async addFiles(files: File[]): Promise<void> {
    const accepted: File[] = [];
    let rejectedImage = false;
    let rejectedVideoType = false;
    const oversizedImages: string[] = [];
    const oversizedVideos: string[] = [];
    const overlongVideos: string[] = [];
    const hdrVideos: string[] = [];
    for (const f of files) {
      if (f.type.startsWith('video/')) {
        const chk = checkUpload(f, 'postVideo'); // mp4 + 200 MB (política central)
        if (!chk.ok) {
          if (chk.reason === 'size') oversizedVideos.push(f.name);
          else rejectedVideoType = true;
          continue;
        }
        let duration: number;
        try {
          duration = await this.readVideoDuration(f);
        } catch {
          // Metadados ilegíveis: rejeita por precaução, alinhado à validação de duração.
          overlongVideos.push(f.name);
          continue;
        }
        if (duration > SocialFeedComponent.MAX_VIDEO_SECONDS) {
          overlongVideos.push(f.name);
          continue;
        }
        if (await isHdrVideo(f)) {
          hdrVideos.push(f.name);
          continue;
        }
        accepted.push(f);
        continue;
      }
      if (f.type.startsWith('image/')) {
        const chk = checkUpload(f, 'postImage'); // jpg/png/webp + 10 MB, sem SVG (política central)
        if (chk.ok) accepted.push(f);
        else if (chk.reason === 'size') oversizedImages.push(f.name);
        else rejectedImage = true;
      }
    }
    if (rejectedImage) {
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('socialFeed.invalidImageType'),
        detail: this.translocoService.translate('socialFeed.invalidImageTypeDetail', { '0': UPLOAD_RULES.postImage.exts.join(', ') }),
      });
    }
    if (oversizedImages.length) {
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('socialFeed.fileTooLarge'),
        detail: this.translocoService.translate('socialFeed.fileTooLargeDetail', { '0': Math.round(UPLOAD_RULES.postImage.maxBytes / (1024 * 1024)) }),
      });
    }
    if (rejectedVideoType) {
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('socialFeed.invalidVideoType'),
        detail: this.translocoService.translate('socialFeed.invalidVideoTypeDetail'),
      });
    }
    if (oversizedVideos.length) {
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('socialFeed.videoTooLarge'),
        detail: this.translocoService.translate('socialFeed.videoTooLargeDetail', {
          '0': oversizedVideos.join(', '),
          '1': 200,
        }),
      });
    }
    if (overlongVideos.length) {
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('socialFeed.videoTooLong'),
        detail: this.translocoService.translate('socialFeed.videoTooLongDetail', {
          '0': overlongVideos.join(', '),
          '1': SocialFeedComponent.MAX_VIDEO_SECONDS,
        }),
      });
    }
    if (hdrVideos.length) {
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('socialFeed.videoHdrNotSupported'),
        detail: this.translocoService.translate('socialFeed.videoHdrNotSupportedDetail', {
          '0': hdrVideos.join(', '),
        }),
      });
    }
    // Limita o total de mídias por publicação (fotos/vídeos).
    const remainingSlots = this.maxMediaCount - this.pendingFiles.length;
    let toAdd = accepted;
    if (accepted.length > remainingSlots) {
      toAdd = accepted.slice(0, Math.max(0, remainingSlots));
      this.messageService.add({
        severity: 'warn',
        summary: this.translocoService.translate('socialFeed.mediaLimit'),
        detail: this.translocoService.translate('socialFeed.mediaLimitDetail', { '0': this.maxMediaCount }),
      });
    }
    if (!toAdd.length) return;
    this.pendingFiles.push(...toAdd);
    this.previewUrls.push(...toAdd.map((f) => URL.createObjectURL(f)));
    this.cropTransforms.push(...toAdd.map(() => defaultCropTransform()));
    this.createStep = "details";
    this.measureFirstMedia();
  }

  /** Lê duração (segundos) via metadados do `<video>`. Rejeita se o navegador não conseguir decodificar. */
  private readVideoDuration(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.muted = true;
      v.playsInline = true;
      const cleanup = () => {
        v.onloadedmetadata = null;
        v.onerror = null;
        URL.revokeObjectURL(url);
      };
      v.onloadedmetadata = () => {
        const d = v.duration;
        cleanup();
        if (!Number.isFinite(d) || d <= 0) {
          reject(new Error('Invalid duration'));
        } else {
          resolve(d);
        }
      };
      v.onerror = () => {
        cleanup();
        reject(new Error('Cannot read metadata'));
      };
      v.src = url;
    });
  }

  removeFile(index: number): void {
    this.pendingFiles.splice(index, 1);
    URL.revokeObjectURL(this.previewUrls[index]);
    this.previewUrls.splice(index, 1);
    this.cropTransforms.splice(index, 1);
    if (this.currentPreview >= this.previewUrls.length) {
      this.currentPreview = Math.max(0, this.previewUrls.length - 1);
    }
    if (!this.pendingFiles.length) {
      this.createStep = "select";
      this.resetPreviewFrame();
    } else {
      this.measureFirstMedia();
    }
  }

  goBackToSelect(): void {
    this.previewUrls.forEach((u) => URL.revokeObjectURL(u));
    this.previewUrls = [];
    this.pendingFiles = [];
    this.currentPreview = 0;
    this.resetPreviewFrame();
    this.createStep = "select";
  }

  private resetPreviewFrame(): void {
    this.firstMediaNaturalW = 0;
    this.firstMediaNaturalH = 0;
    this.previewFrameW = 0;
    this.previewFrameH = 0;
  }

  /** Lê largura/altura da primeira mídia; o quadro do carrossel segue essa proporção (escalada). */
  private measureFirstMedia(): void {
    this.firstMediaNaturalW = 0;
    this.firstMediaNaturalH = 0;
    if (!this.pendingFiles.length || !this.previewUrls.length) {
      this.updatePreviewFrameSize();
      return;
    }
    const file = this.pendingFiles[0];
    const url = this.previewUrls[0];

    if (file.type.startsWith("image/")) {
      const img = new Image();
      img.onload = () => {
        this.firstMediaNaturalW = img.naturalWidth;
        this.firstMediaNaturalH = img.naturalHeight;
        this.updatePreviewFrameSize();
      };
      img.onerror = () => {
        this.firstMediaNaturalW = 3;
        this.firstMediaNaturalH = 2;
        this.updatePreviewFrameSize();
      };
      img.src = url;
      return;
    }

    if (file.type.startsWith("video/")) {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.muted = true;
      v.playsInline = true;
      v.onloadedmetadata = () => {
        this.firstMediaNaturalW = v.videoWidth || 1280;
        this.firstMediaNaturalH = v.videoHeight || 720;
        this.updatePreviewFrameSize();
      };
      v.onerror = () => {
        this.firstMediaNaturalW = 16;
        this.firstMediaNaturalH = 9;
        this.updatePreviewFrameSize();
      };
      v.src = url;
      return;
    }

    this.updatePreviewFrameSize();
  }

  /** Escala o quadro da primeira mídia para caber no modal; demais slides usam o mesmo retângulo + object-fit: contain. */
  private updatePreviewFrameSize(): void {
    if (!this.firstMediaNaturalW || !this.firstMediaNaturalH) {
      this.previewFrameW = 0;
      this.previewFrameH = 0;
      return;
    }
    const vw = typeof window !== "undefined" ? window.innerWidth : 860;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    const isNarrow = vw <= 768;
    const isTiny = vw <= 400;
    let maxW: number;
    let maxH: number;
    if (isTiny) {
      maxW = Math.max(120, vw - 16);
      maxH = Math.min(vh * 0.5, vh - 88);
    } else if (isNarrow) {
      maxW = Math.max(160, vw - 24);
      maxH = Math.min(420, vh * 0.48, vh - 96);
    } else {
      maxW = Math.min(520, vw * 0.44);
      maxH = Math.min(vh * 0.62, vh - 140);
    }
    const scale = Math.min(maxW / this.firstMediaNaturalW, maxH / this.firstMediaNaturalH, 1);
    this.previewFrameW = Math.max(1, Math.round(this.firstMediaNaturalW * scale));
    this.previewFrameH = Math.max(1, Math.round(this.firstMediaNaturalH * scale));
  }

  prevPreview(): void {
    this.currentPreview = Math.max(0, this.currentPreview - 1);
  }

  nextPreview(): void {
    this.currentPreview = Math.min(this.previewUrls.length - 1, this.currentPreview + 1);
  }

  private previewTouchStartX = 0;
  private previewTouchStartY = 0;

  onPreviewTouchStart(e: TouchEvent): void {
    this.previewTouchStartX = e.changedTouches[0].clientX;
    this.previewTouchStartY = e.changedTouches[0].clientY;
  }

  onPreviewTouchEnd(e: TouchEvent): void {
    const dx = e.changedTouches[0].clientX - this.previewTouchStartX;
    const dy = e.changedTouches[0].clientY - this.previewTouchStartY;
    if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0) {
      this.nextPreview();
    } else {
      this.prevPreview();
    }
  }

  isFileVideo(index: number): boolean {
    return this.pendingFiles[index]?.type.startsWith("video/") ?? false;
  }

  async submitCreate(): Promise<void> {
    const uid = this.auth.getUserInformations();
    if (!uid) {
      this.messageService.add({ severity: "warn", summary: this.translocoService.translate('socialFeed.sessionError') });
      return;
    }
    if (!this.pendingFiles.length) {
      this.messageService.add({ severity: "warn", summary: this.translocoService.translate('socialFeed.postLabel'), detail: this.translocoService.translate('socialFeed.noMedia') });
      return;
    }
    if (!this.selectedProjectId) {
      this.messageService.add({
        severity: "warn",
        summary: this.translocoService.translate('socialFeed.postLabel'),
        detail: this.translocoService.translate('socialFeed.selectProjectFirst'),
      });
      return;
    }
    this.creating = true;
    this.creatingStep = 'uploading';
    this.creatingFileTotal = this.pendingFiles.length;
    this.creatingFileIndex = 0;
    const mediaIds: string[] = [];

    // Recorta as IMAGENS conforme o editor (proporção/zoom/posição) -> sobe o arquivo já cortado.
    // Vídeos passam direto (não são redimensionados).
    const filesToUpload: File[] = [];
    for (let i = 0; i < this.pendingFiles.length; i++) {
      const f = this.pendingFiles[i];
      if (f.type.startsWith("image/")) {
        filesToUpload.push(await cropImageToFile(f, this.postAspect, this.cropTransforms[i] ?? defaultCropTransform()));
      } else {
        filesToUpload.push(f);
      }
    }

    try {
      for (let i = 0; i < this.pendingFiles.length; i++) {
        const file = filesToUpload[i];
        this.creatingFileIndex = i + 1;
        const resp = await lastValueFrom(
          this.social.uploadMediaRegister({ file: file.name })
        );
        await this.social.uploadFileMultipart(uid, resp.objectKey, file);
        mediaIds.push(resp.mediaId);
      }

      this.creatingStep = 'publishing';

      const payload: SocialCreatePostRequest = {
        caption: this.createCaption || null,
        projectId: this.selectedProjectId,
        mediaIds,
      };

      const newPost = await lastValueFrom(this.social.createPost(payload));

      this.creating = false;
      this.creatingStep = null;
      this.creatingFileIndex = 0;
      this.creatingFileTotal = 0;
      this.createVisible = false;
      this.messageService.add({ severity: "success", summary: this.translocoService.translate('socialFeed.postCreated') });

      // Salva blob URLs locais antes de limpar o estado do modal.
      // São usadas para exibição imediata — apontam para os arquivos do dispositivo
      // e funcionam instantaneamente, sem depender da propagação do S3.
      // Serão revogadas automaticamente na próxima chamada a loadFeed(true).
      // Exibição imediata usa as mídias JÁ RECORTADAS (filesToUpload) — assim bate com o
      // que foi enviado (proporção/zoom/posição), sem precisar de F5. As previews originais
      // (não recortadas) são revogadas aqui.
      const blobUrls = filesToUpload.map((f) => URL.createObjectURL(f));
      this.previewUrls.forEach((u) => URL.revokeObjectURL(u));
      this.previewUrls = []; // limpa estado
      this._pendingBlobUrls = blobUrls;

      if (newPost) {
        const immediatePost: typeof newPost = {
          ...newPost,
          media: newPost.media?.length
            ? newPost.media.map((m, i) => ({
                ...m,
                url: blobUrls[i] ?? m.url,
                thumbnailUrl: blobUrls[i] ?? m.thumbnailUrl ?? null,
              }))
            : blobUrls.map((url, i) => ({
                id: `local-${i}`,
                mediaType: (this.pendingFiles[i]?.type.startsWith('video/') ? 'Video' : 'Image') as 'Image' | 'Video',
                url,
                thumbnailUrl: url,
                sortOrder: i,
              })),
        };
        this.posts = [immediatePost, ...this.posts];
      }

      this.loadExplore(true);
    } catch (err) {
      this.creating = false;
      this.creatingStep = null;
      this.creatingFileIndex = 0;
      this.creatingFileTotal = 0;
      this.messageService.add({
        severity: "error",
        summary: this.translocoService.translate('socialFeed.postLabel'),
        detail: SocialService.parseHttpErrorMessage(err),
      });
    }
  }
}
